import type { CoBuyer, IShopper, GenericObject } from '@makemydeal/dr-platform-types';
import type { AnyAction } from 'redux';
import * as types from '../types/actionTypes';
import { emailCaseCorrect, formatPhoneNumber } from '../utils/shopperUtils';
import { transformCommonConsumerToCoBuyer } from '../utils/coBuyerUtils';
import { initialState } from './initialState';
import {
    getParticipant,
    getQualifiedPhoneNumbers,
    toShopperAddress,
    toShopperEmployment,
    splitMonthsToYearsAndMonths
} from '../utils/dxgUtils';

export function coBuyer(state: any = null, action: AnyAction) {
    // NOTE: redux is forcing us to use null because we're not allowed to return undefined.
    switch (action.type) {
        case types.INIT_INVALID_OFFER:
        case types.INIT_OFFER: {
            return action.payload.coBuyer ? { ...action.payload.coBuyer } : null;
        }
        case types.CREDIT_APP_UPDATED_CO_BUYER: {
            return { ...state, ...action.payload };
        }
        case types.SWAPPED_BUYER_COBUYER: {
            return { ...action.payload.coBuyer };
        }
        case types.UPDATED_COBUYER: {
            if (action.payload == null) return null;

            // transform CommonConsumer to CoBuyer
            const coBuyer: CoBuyer = transformCommonConsumerToCoBuyer(
                action.meta.replace ? {} : state ?? {},
                action.payload,
                action.meta.allowEmptyValues,
                action.meta.optionalDesking,
                action.meta.isPostalCodeDigitsOnlyValidationEnabled
            );

            return { ...coBuyer };
        }
        case types.CREDIT_APP_SEARCH_MERGE_DEAL: {
            const dealCoBuyer = action.payload?.dealStructure?.participants?.coBuyer;
            // Remove cobuyer if not present in merging deal
            if (!dealCoBuyer) return null;

            const coBuyerGlobalParticipant = getParticipant(
                dealCoBuyer,
                action.payload.participants,
                action.meta?.enableCorrectPhoneHandling
            );
            const { years: coBuyerYearsAtCurrentAddress, months: coBuyerMonthsAtCurrentAddress } = splitMonthsToYearsAndMonths(
                coBuyerGlobalParticipant?.primaryAddress?.monthsAtCurrentAddress
            );
            const { years: coBuyerYearsAtPreviousAddress, months: coBuyerMonthsAtPreviousAddress } = splitMonthsToYearsAndMonths(
                coBuyerGlobalParticipant?.previousAddress?.monthsAtPreviousAddress
            );

            return {
                firstName: dealCoBuyer.firstName,
                lastName: dealCoBuyer.lastName,
                middleName: dealCoBuyer.middleName,
                title: dealCoBuyer.salutation,
                suffix: dealCoBuyer.suffix,
                email: dealCoBuyer.emailAddress && emailCaseCorrect(dealCoBuyer.emailAddress),
                phone: dealCoBuyer.phoneNumber,
                ...(action.meta?.enableCorrectPhoneHandling && getQualifiedPhoneNumbers(coBuyerGlobalParticipant)),
                zip: dealCoBuyer.address?.postalCode,
                county: dealCoBuyer.address?.countyDistrict,
                city: dealCoBuyer.address?.city,
                state: dealCoBuyer.address?.stateProvince,
                streetAddress: dealCoBuyer.address?.addressLine1,
                commonConsumerId: dealCoBuyer.commonConsumerId,
                relationshipToBuyer: dealCoBuyer.relationshipToBuyer,
                participantRole: dealCoBuyer.participantRole,
                estimatedCreditScore: dealCoBuyer.estimatedCreditScore,
                ...(coBuyerGlobalParticipant?.birthDate && { birthDate: coBuyerGlobalParticipant.birthDate }),
                ...(coBuyerGlobalParticipant?.spouse && { spouse: coBuyerGlobalParticipant.spouse }),
                ...(coBuyerGlobalParticipant?.maritalStatus !== undefined && {
                    maritalStatus: coBuyerGlobalParticipant.maritalStatus
                }),
                ...(coBuyerGlobalParticipant?.creditPullConsentGiven !== undefined && {
                    creditPullConsentGiven: coBuyerGlobalParticipant.creditPullConsentGiven
                }),
                ...(coBuyerGlobalParticipant?.financialInfo && { financialInfo: coBuyerGlobalParticipant.financialInfo }),
                ...(coBuyerGlobalParticipant?.housingInfo && { housingInfo: coBuyerGlobalParticipant.housingInfo }),
                ...(coBuyerMonthsAtCurrentAddress !== undefined && { monthsAtCurrentAddress: coBuyerMonthsAtCurrentAddress }),
                ...(coBuyerYearsAtCurrentAddress !== undefined && { yearsAtCurrentAddress: coBuyerYearsAtCurrentAddress }),
                ...(coBuyerMonthsAtPreviousAddress !== undefined && { monthsAtPreviousAddress: coBuyerMonthsAtPreviousAddress }),
                ...(coBuyerYearsAtPreviousAddress !== undefined && { yearsAtPreviousAddress: coBuyerYearsAtPreviousAddress }),
                ...(coBuyerGlobalParticipant?.previousAddress && {
                    previousAddress: toShopperAddress(coBuyerGlobalParticipant.previousAddress)
                }),
                ...(coBuyerGlobalParticipant?.employmentInfo?.currentEmployment && {
                    currentEmployment: toShopperEmployment(coBuyerGlobalParticipant.employmentInfo.currentEmployment)
                }),
                ...(coBuyerGlobalParticipant?.employmentInfo?.previousEmployment && {
                    previousEmployment: toShopperEmployment(coBuyerGlobalParticipant.employmentInfo.previousEmployment)
                }),
                contactPreferences: state?.contactPreferences || {
                    email: false,
                    phone: false,
                    sms: false
                }
            };
        }
        default: {
            return state;
        }
    }
}

export function shopper(state: IShopper = initialState.shopper, action: AnyAction) {
    switch (action.type) {
        case types.INIT_INVALID_OFFER:
        case types.INIT_OFFER: {
            return { ...state, ...action.payload.shopper };
        }
        case types.UPDATED_SHOPPER_CONTACT: {
            return action.meta?.replace ? { ...action.payload } : { ...state, ...action.payload };
        }
        case types.UPDATED_SHOPPER: {
            const shopper = action.payload.shopper;
            if (shopper && shopper.email) {
                shopper.email = emailCaseCorrect(shopper.email);
            }
            return { ...state, ...action.payload.shopper };
        }
        case types.SWAPPED_BUYER_COBUYER: {
            return { ...action.payload.shopper };
        }
        case types.SYNC_MESSAGE: {
            return { ...state, ...action.message.offer.shopper };
        }
        case types.UPDATED_SHOPPER_ZIP: {
            return { ...state, ...{ zip: action.payload } };
        }
        case types.ZIP_EDIT_TERMS_BTN_CLICKED: {
            return { ...state, ...{ zip: action.payload.zipCode } };
        }
        case types.FETCH_QUESTIONS_OR_PAYMENT: {
            return { ...state, ...{ zip: action?.payload?.zipCode } };
        }
        case types.ICO_TRADE_IN_COMPLETED: {
            const { email = '', firstName = '', lastName = '', phone = '' } = action.payload || {};
            const validPhoneNumber = formatPhoneNumber(phone);
            const newShopper = {
                ...state,
                email: email ? emailCaseCorrect(email) : state.email,
                firstName: firstName || state.firstName,
                lastName: lastName || state.lastName,
                phone: validPhoneNumber || state.phone
            };
            return newShopper;
        }
        case types.CREDIT_APP_UPDATED_SHOPPER: {
            return { ...state, ...action.payload };
        }
        case types.CREDIT_APP_SEARCH_MERGE_DEAL: {
            const buyer = action.payload?.dealStructure?.participants?.buyer;
            if (!buyer) return state;

            const buyerData: GenericObject = { ...buyer };
            if (buyerData.businessSigner) {
                Object.assign(buyerData, buyerData.businessSigner);
            }
            if (buyerData.legalName && buyerData.businessEntityId) {
                buyerData.reduxShopperBusinessData = {
                    business: {
                        legalName: buyerData.legalName,
                        businessEntityId: buyerData.businessEntityId
                    }
                };
            }
            const globalParticipant = getParticipant(buyer, action.payload.participants, action.meta?.enableCorrectPhoneHandling);
            const customerType = globalParticipant?.customerType || 'Individual';
            const dxgBillingAddress = globalParticipant?.additionalAddresses?.find((address) => address.addressType === 'Billing');
            const { years: yearsAtCurrentAddress, months: monthsAtCurrentAddress } = splitMonthsToYearsAndMonths(
                globalParticipant?.primaryAddress?.monthsAtCurrentAddress
            );
            const { years: yearsAtPreviousAddress, months: monthsAtPreviousAddress } = splitMonthsToYearsAndMonths(
                globalParticipant?.previousAddress?.monthsAtPreviousAddress
            );

            return {
                ...buyerData.reduxShopperBusinessData,
                customerType,
                firstName: buyerData.firstName,
                middleName: buyerData.middleName,
                lastName: buyerData.lastName,
                title: buyerData.salutation,
                suffix: buyerData.suffix,
                email: buyerData.emailAddress && emailCaseCorrect(buyerData.emailAddress),
                phone: buyerData.phoneNumber,
                ...(action.meta?.enableCorrectPhoneHandling && getQualifiedPhoneNumbers(globalParticipant)),
                zip: buyerData.address?.postalCode,
                city: buyerData.address?.city,
                county: buyerData.address?.countyDistrict,
                state: buyerData.address?.stateProvince,
                streetAddress: buyerData.address?.addressLine1,
                streetAddress2: buyerData.address?.addressLine2,
                streetAddress3: buyerData.address?.addressLine3,
                commonConsumerId: buyerData.commonConsumerId,
                spouse: globalParticipant?.spouse,
                ...(globalParticipant?.birthDate && { birthDate: globalParticipant.birthDate }),
                estimatedCreditScore: buyerData.estimatedCreditScore,
                pixallId: globalParticipant?.pixallId,
                ...(globalParticipant?.maritalStatus !== undefined && { maritalStatus: globalParticipant.maritalStatus }),
                ...(globalParticipant?.creditPullConsentGiven !== undefined && {
                    creditPullConsentGiven: globalParticipant.creditPullConsentGiven
                }),
                ...(globalParticipant?.financialInfo && { financialInfo: globalParticipant.financialInfo }),
                ...(globalParticipant?.housingInfo && { housingInfo: globalParticipant.housingInfo }),
                ...(monthsAtCurrentAddress !== undefined && { monthsAtCurrentAddress }),
                ...(yearsAtCurrentAddress !== undefined && { yearsAtCurrentAddress }),
                ...(monthsAtPreviousAddress !== undefined && { monthsAtPreviousAddress }),
                ...(yearsAtPreviousAddress !== undefined && { yearsAtPreviousAddress }),
                ...(globalParticipant?.previousAddress && { previousAddress: toShopperAddress(globalParticipant.previousAddress) }),
                ...(globalParticipant?.employmentInfo?.currentEmployment && {
                    currentEmployment: toShopperEmployment(globalParticipant.employmentInfo.currentEmployment)
                }),
                ...(globalParticipant?.employmentInfo?.previousEmployment && {
                    previousEmployment: toShopperEmployment(globalParticipant.employmentInfo.previousEmployment)
                }),
                ...(dxgBillingAddress && {
                    billingAddress: {
                        streetAddress: dxgBillingAddress.addressLine1,
                        streetAddress2: dxgBillingAddress.addressLine2,
                        streetAddress3: dxgBillingAddress.addressLine3,
                        zip: dxgBillingAddress.postalCode,
                        city: dxgBillingAddress.city,
                        county: dxgBillingAddress.countyDistrict,
                        state: dxgBillingAddress.stateProvince
                    }
                }),
                contactPreferences: state?.contactPreferences || {
                    email: false,
                    phone: false,
                    sms: false
                }
            };
        }
        default: {
            return state;
        }
    }
}

export function message(state: string | undefined = initialState.message, action: AnyAction) {
    switch (action.type) {
        case types.INIT_SUCCESS:
            return action.payload.offer.messageToDealer || state;
        case types.UPDATED_SHOPPER:
        case types.UPDATED_SHOPPER_MESSAGE:
            return action.payload.message;
        case types.SYNC_MESSAGE:
            return action.message.offer.message || state;
        case types.UPDATED_COUNTEROFFER_MESSAGE:
            return action.payload;
        default:
            return state;
    }
}

export function vehicleFulfillment(state: any = initialState.vehicleFulfillment, action: AnyAction) {
    switch (action.type) {
        case types.INIT_SUCCESS:
            return action.payload.offer.vehicleFulfillment || state;
        case types.CLEAR_VEHICLE_FULFILLMENT:
            return null;
        default:
            return state;
    }
}

export function participantsSPD(state: any = initialState.participantsSPD, action: AnyAction) {
    switch (action.type) {
        case types.INIT_SUCCESS:
            return action.payload.offer.participantsSPD || state;
        default:
            return state;
    }
}

export function commonOrgId(state: string | undefined = initialState.commonOrgId, action: AnyAction) {
    switch (action.type) {
        case types.INIT_INVALID_DEAL_SUCCESS:
        case types.INIT_SUCCESS:
        case types.CCID_CORGID_UPDATED:
        case types.OFFER_SUBMISSION_SUCCESS:
            return action.payload.offer.commonOrgId || '';
        default:
            return state;
    }
}

export function commonConsumerId(state: string | undefined = initialState.commonConsumerId, action: AnyAction) {
    switch (action.type) {
        case types.INIT_INVALID_DEAL_SUCCESS:
        case types.INIT_SUCCESS:
        case types.CCID_CORGID_UPDATED:
        case types.OFFER_SUBMISSION_SUCCESS: {
            return action.payload.offer.commonConsumerId || '';
        }
        case types.BUSINESS_CONVERSION_SUCCESS: {
            return action.payload?.commonConsumerId || state;
        }
        case types.CREDIT_APP_SEARCH_MERGE_DEAL: {
            return action.payload?.dealStructure?.participants?.buyer?.commonConsumerId || state;
        }
        case types.SWAPPED_BUYER_COBUYER: {
            return action.payload.shopper?.commonConsumerId || '';
        }
        case types.UPDATED_SHOPPER_CONTACT: {
            return action.meta?.replace ? action.payload?.commonConsumerId || state : state;
        }
        default: {
            return state;
        }
    }
}

export const cmdMessageToShopper = (state: string | undefined = initialState.cmdMessageToShopper, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_SUCCESS:
            return action.payload.offer.cmdMessageToShopper || null;
        case types.UPDATED_DEALER_MESSAGE:
            return action.payload.cmdMessageToShopper;
        default:
            return state;
    }
};

export const cmdMessageToSalesperson = (state: string | undefined = initialState.cmdMessageToSalesperson, action: AnyAction) => {
    switch (action.type) {
        case types.UPDATE_MESSAGE_TO_SALESPERSON:
            return action.payload;
        default:
            return state;
    }
};

export function dealerUser(state: string | undefined = initialState.dealerUser, action: AnyAction) {
    switch (action.type) {
        case types.INIT_SUCCESS:
        case types.OFFER_SUBMISSION_SUCCESS:
            return action.payload.offer.dealerUser || '';
        default:
            return state;
    }
}
